/*
 * Social proof video carousel.
 * On mobile the track scrolls horizontally with scroll-snap; this element
 * builds pagination dots, keeps the active dot in sync with scroll position,
 * and scrolls to a card when its dot is clicked. On desktop (grid, no
 * overflow) the dots hide themselves. No external dependencies.
 */
if (!customElements.get('social-proof-carousel')) {
  customElements.define(
    'social-proof-carousel',
    class SocialProofCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.dotsWrap = this.querySelector('[data-dots]');
        if (!this.track || !this.dotsWrap) return;

        this.cards = Array.from(this.track.querySelectorAll('.social-proof__card'));
        if (this.cards.length < 2) return;

        this.build();

        this.onScroll = this.throttle(() => this.setActive(), 100);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        this.onResize = this.throttle(() => this.build(), 200);
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        if (this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
      }

      isScrollable() {
        return this.track.scrollWidth - this.track.clientWidth > 2;
      }

      build() {
        this.dotsWrap.innerHTML = '';
        if (!this.isScrollable()) {
          this.dotsWrap.style.display = 'none';
          return;
        }
        this.dotsWrap.style.display = '';
        this.dots = this.cards.map((card, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'social-proof__dot';
          dot.setAttribute('aria-label', 'Go to video ' + (index + 1));
          dot.addEventListener('click', () => {
            this.track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
          });
          this.dotsWrap.appendChild(dot);
          return dot;
        });
        this.setActive();
      }

      setActive() {
        if (!this.dots) return;
        const scrollLeft = this.track.scrollLeft;
        let active = 0;
        let min = Infinity;
        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft - scrollLeft);
          if (distance < min) {
            min = distance;
            active = index;
          }
        });
        this.dots.forEach((dot, index) => {
          if (index === active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      throttle(fn, wait) {
        let last = 0;
        let timer = null;
        return () => {
          const now = Date.now();
          const remaining = wait - (now - last);
          if (remaining <= 0) {
            if (timer) {
              clearTimeout(timer);
              timer = null;
            }
            last = now;
            fn();
          } else if (!timer) {
            timer = setTimeout(() => {
              last = Date.now();
              timer = null;
              fn();
            }, remaining);
          }
        };
      }
    }
  );
}
